// apps/frontend/src/lib/navigate.ts
// Helper de navigation programmatique compatible BrowserRouter.
//
// Avant : on faisait `window.location.hash = '/seller/X'` partout (31 sites)
// avec createHashRouter. Apres migration vers createBrowserRouter, hash ne
// declenche plus la navigation react-router. Ce helper fait history.pushState
// + dispatch popstate pour que react-router catche le changement et re-rend.
//
// Avantage vs window.location.assign(path) : pas de full reload, garde le
// state React (modals ouvertes, Sentry user context, websockets, etc.)
// = experience instantanee comme avant.
//
// Accepte 2 formats pour migration douce :
//   glowpNavigate('/seller/123')     // path absolu standard
//   glowpNavigate('#/seller/123')    // ancien format hash (compat)
//   glowpNavigate('/seller/123?ref=x') // avec query
//
// Tous sont normalises en path absolu pour BrowserRouter.

export function glowpNavigate(target: string): void {
  // Compat : strip le # de tete si present (vestige des appels en
  // window.location.hash = '#/x')
  let path = target.startsWith('#') ? target.slice(1) : target;
  // Garantie : doit commencer par /
  if (!path.startsWith('/')) path = '/' + path;

  // Si on est deja sur exactement cette URL, ne fait rien (evite re-render
  // inutile + scroll reset).
  if (window.location.pathname + window.location.search === path) return;

  window.history.pushState({}, '', path);
  // Notifie react-router via l'event popstate (react-router-dom v6 listen ce
  // type d'event). dispatchEvent est sync, le re-render se fait au prochain
  // tick.
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// ============================================================================
// smartBack — retour intelligent
// ============================================================================
//
// Probleme : navigate('/home') sur un bouton retour fait toujours pareil
// (force home), peu importe d'ou vient l'user. UX cassee : "je viens de
// scroller le catalogue, je clique retour, je veux retourner ou j'etais
// pas la home".
//
// Solution : navigate(-1) (vrai retour browser) si possible, fallback
// vers une route raisonnable si pas d'historique (user arrive par URL
// directe / partage / push notif).
//
// On lit window.history.state.idx, l'index maintenu par React Router dans
// sa pile de navigation. window.history.length n'etait pas fiable : il
// compte aussi les pages d'autres sites ouvertes avant dans le meme onglet
// (un navigate(-1) pouvait donc faire QUITTER Glowp).

import type { NavigateFunction } from 'react-router-dom';

export function smartBack(
  navigate: NavigateFunction,
  fallback: string = '/'
): void {
  // idx > 0      -> l'user a navigue dans l'app : navigate(-1) reste sur Glowp.
  // idx 0/absent -> arrivee directe (lien Insta, push, URL tapee) : on envoie
  //                 vers le fallback plutot que de quitter le site ou de ne
  //                 rien faire.
  const idx =
    typeof window !== 'undefined'
      ? (window.history.state as { idx?: number } | null)?.idx
      : undefined;
  if (typeof idx === 'number' && idx > 0) {
    navigate(-1);
  } else {
    navigate(fallback);
  }
}
